<template>
  <div data-tour="2" class="flex flex-col gap-y-2 w-full">
    <h1
      v-if="!mobile"
      class="text-blue-600 text-xl font-bold"
      :data-tour-override="tourIsActive ? 'text-blue-600 z-10' : undefined"
    >
      I want to find services near me
    </h1>
    <div
      :class="{ 'flex flex-col sm:flex-row items-center': frontEnd, 'flex-col sm:flex-col': stacked }"
      class="relative w-full"
      :data-tour-override="tourIsActive ? 'flex-col' : undefined"
    >
      <div
        :class="{ 'sm:px-4 py-3': frontEnd }"
        class="pb-3 rounded-l-lg w-full flex gap-1 items-center justify-between sm:bg-white"
        :data-tour-override="tourIsActive ? 'bg-transparent' : undefined"
      >
        <UInput
          v-model="query"
          tabindex="6"
          :variant="mobile ? 'outline' : 'none'"
          :placeholder="placeholder"
          :icon="mobile ? 'i-mdi-search' : undefined"
          trailing
          :size="mobile ? 'xl' : 'md'"
          autocomplete="nope"
          class="w-full"
          aria-label="Search for a location"
          @focus="onAddressFocus"
          @blur="onAddressBlur"
          @keydown.down.prevent="onArrowDown"
          @keydown.up.prevent="onArrowUp"
          @keydown.enter.prevent="onEnter"
        />
        <UButton
          v-if="!mobile && frontEnd"
          tabindex="7"
          class="rounded-full bg-[#d9e3f0] hover:bg-[#d9e3f0] text-black font-light"
          size="xs"
          icon="i-ion-pinpoint"
          trailing
          :loading="isLoading"
          aria-label="Use my current location"
          @click="getLocation"
        >
          <span class="hidden md:block"> OR use my current location </span>
        </UButton>
      </div>

      <!-- mobile button -->
      <div v-if="mobile && frontEnd" :class="{ 'sm:px-4': stacked }" class="w-full">
        <UButton
          class="rounded-full bg-[#d9e3f0] hover:bg-[#d9e3f0] text-black font-light"
          icon="i-ion-pinpoint"
          trailing
          size="lg"
          :loading="isLoading"
          block
          :ui="{ padding: { lg: 'py-1' }, font: 'font-light' }"
          aria-label="Use my current location"
          @click="getLocation"
        >
          <span> OR use my current location </span>
        </UButton>
      </div>

      <!-- desktop search button -->
      <UButton
        v-if="!mobile"
        tabindex="8"
        label="Search"
        color="green"
        class="rounded-t-none sm:rounded-tr-lg sm:rounded-l-none w-full sm:w-fit px-2 sm:px-4 xl:px-12 h-14 justify-center mr-1"
        :size="iframeConfig?.uid ? 'md' : 'xl'"
        icon="i-heroicons-magnifying-glass-20-solid"
        aria-label="Search for a location"
      />

      <!-- Dynamic Suggestions Dropdown -->
      <div
        v-if="showSuggestions"
        ref="suggestionsElement"
        class="top-full left-0 right-0 -mt-10 md:mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto z-30"
        :class="{ absolute: frontEnd }"
      >
        <ul class="py-2">
          <li
            v-for="(item, index) in suggestions"
            :key="item.place_id"
            :class="[
              'px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-x-3',
              { 'bg-gray-100': index === highlightedIndex }
            ]"
            @click="handleSelect(item)"
            @keydown.enter="handleSelect(item)"
            @mouseover="highlightedIndex = index"
          >
            <i class="i-ion-pinpoint text-gray-400"></i>
            <span>{{ item.description }}</span>
          </li>
        </ul>
        <div class="flex justify-end py-1 px-2">
          <img
            class="h-4"
            src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
          />
        </div>
      </div>
    </div>
    <div v-if="error" class="text-red-600">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
import { usePlacesAutocomplete } from 'vue-use-places-autocomplete'
import { onClickOutside } from '@vueuse/core'
import { Geolocation } from '@capacitor/geolocation'
import type { GoogleAutocompleteSuggestion } from '~/types/GoogleAutocompleteSuggestion'

const CURRENT_LOCATION_TEXT = 'Current Location'

const props = defineProps({
  mobile: {
    type: Boolean,
    required: false,
    default: false
  },
  frontEnd: {
    type: Boolean,
    required: false,
    default: true
  },
  stacked: Boolean
})

// determine if we are in an iframe
// this comes from the store where we set the org id
const { iframeConfig } = storeToRefs(useIframeConfigStore())

const isLoading = ref(false)
const error = ref('')
const config = useRuntimeConfig().public
const suggestionsElement = ref<HTMLElement | null>(null)
const showSuggestions = ref(false)
const highlightedIndex = ref(-1)

const { isActive: tourIsActive } = storeToRefs(useOnboardingTourStore())

const { locationSearch } = storeToRefs(useServiceSearchStore())

const query = computed({
  get: () => {
    return locationSearch.value
  },
  set: (val: string) => {
    locationSearch.value = val
  }
})

const addressSearchIsFocused = ref(false)
const onAddressFocus = () => {
  addressSearchIsFocused.value = true
}
const onAddressBlur = () => {
  setTimeout(() => {
    addressSearchIsFocused.value = false
  }, 100)
}

const onArrowDown = () => {
  if (highlightedIndex.value < suggestions.value.length - 1) {
    highlightedIndex.value++
  }
}

const onArrowUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

const onEnter = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < suggestions.value.length) {
    handleSelect(suggestions.value[highlightedIndex.value])
  }
}

// wrap around query to only run when address search is focused
// this is used in usePlacesAutocomplete
const queryFocused = computed(() => {
  const isFocused = addressSearchIsFocused.value
  // we say it's the same as current location if it has 90% similarity
  const currentLoactionTrimmed = CURRENT_LOCATION_TEXT.slice(0, -2)
  const isCurrentLocation = query.value.startsWith(currentLoactionTrimmed)
  return isFocused && !isCurrentLocation ? query.value : ''
})

const { suggestions, sessionToken, refreshSessionToken } = usePlacesAutocomplete(queryFocused, {
  apiKey: config.isNative ? config.mapsApiKeyNative : config.mapsApiKey,
  debounce: 500,
  minLengthAutocomplete: 2,
  apiOptions: {
    region: 'uk'
  },
  withSessionToken: true,
  autocompletionRequest: {
    types: ['geocode'],
    componentRestrictions: {
      country: ['uk', 'IE', 'IM', 'JE', 'GG', 'GBA', 'GB-IOW', 'GB-ELS', 'GB-ZET', 'L0', '']
    }
  }
})

// Show suggestions when there are results and query exists
watch([suggestions, query], ([newSuggestions, newQuery]) => {
  showSuggestions.value = newSuggestions.length > 0 && newQuery.length > 0
})

// Scroll to suggestions element when it's shown in back-end filters
watch(suggestionsElement, () => {
  if (!props.frontEnd) suggestionsElement.value?.scrollIntoView({ behavior: 'smooth' })
})

// Handle click outside
onClickOutside(suggestionsElement, () => {
  showSuggestions.value = false
})

const { location, selectedLocationSuggestion } = storeToRefs(useServicesLocationStore())

const emit = defineEmits<{
  (key: 'current-location-obtained', value: { latitude: number; longitude: number }): void
  (
    key: 'location-selected',
    value: { latitude: number; longitude: number; item: GoogleAutocompleteSuggestion }
  ): void
}>()

const handleSelect = async (item: GoogleAutocompleteSuggestion) => {
  const res = await getLatLngFromPlaceId({ placeId: item.place_id, sessionToken: sessionToken.value })
  if (!res) {
    console.error('Failed to get coordinates from place id' + item.place_id)
    useSnack().error('Failed to get coordinates from that address. Please try another address.')
    return
  }

  // if we get a response, we then need to reset the session token
  refreshSessionToken()

  const { lat, lng } = res
  location.value = { latitude: lat, longitude: lng }
  selectedLocationSuggestion.value = item

  emit('location-selected', { latitude: lat, longitude: lng, item })

  // Hide suggestions after selection
  nextTick(async () => {
    useGaEvent('location_search', useGaEventHelpers().getGaUserLocationData())
    showSuggestions.value = false
    await until(addressSearchIsFocused).toBe(false)
    query.value = item.description
  })
}

const getLocation = async () => {
  try {
    isLoading.value = true
    error.value = ''
    const isNative = useRuntimeConfig().public.isNative
    const coords = isNative ? await getNativeLocation() : await getWebLocation()
    location.value = coords
    emit('current-location-obtained', coords)
    useGaEvent('use_current_location', { status: 'success', lat: coords.latitude, lng: coords.longitude })

    query.value = CURRENT_LOCATION_TEXT
    // clear out any previously selected location suggestion
    selectedLocationSuggestion.value = undefined
  } catch (e: unknown) {
    console.error(e)
    const err = e as { code?: number; message?: string }
    let m = err?.message || 'Failed to get address from coordinates'
    if (err?.code === 1) {
      m = 'Location access denied. Please enable location services.'
    }
    error.value = m
  } finally {
    isLoading.value = false
  }
}

const getNativeLocation = async () => {
  const coordinates = await Geolocation.getCurrentPosition()
  return {
    latitude: coordinates.coords.latitude,
    longitude: coordinates.coords.longitude
  }
}

const getWebLocation = async () => {
  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by your browser')
  }

  const coords: GeolocationCoordinates = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      (err) => reject(err)
    )
  })

  return {
    latitude: coords.latitude,
    longitude: coords.longitude
  }
}

const placeholder = computed(() =>
  props.mobile ? 'Address, city or postcode' : 'Search by address, city or postcode'
)
</script>
